import * as React from 'react';
import BasePhoneInput, {
  type Country,
  type DefaultInputComponentProps,
  type FlagProps,
  type Props as BasePhoneInputProps,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { cn } from '@/lib/utils';

/** The library's own `Flag` type isn't exported; mirror its shape. */
type FlagComponent = (props: FlagProps) => React.JSX.Element;

/**
 * PhoneInput — form-field wrapper around `react-phone-number-input`, styled to
 * sit next to the shadcn `Input` (same border, radius, focus ring). The country
 * dropdown lives inside the bordered box, so the whole control reads as one
 * field. `value` is E.164; `onChange` reports `undefined` while incomplete.
 *
 * Table cells use `components/Table/editors/PhoneCell` instead — same widget,
 * but stripped of this chrome so it sits flush in a row.
 */
/**
 * The library types its pass-through props with an index signature
 * (`DefaultInputComponentProps`), which collapses handler types to `any` and
 * leaves callers writing implicitly-typed params. Re-declare the handlers we
 * care about so callsites get real types.
 */
type PhoneInputProps = Omit<
  BasePhoneInputProps<DefaultInputComponentProps>,
  'flagComponent' | 'defaultCountry' | 'onChange' | 'onKeyDown'
> & {
  flagComponent?: FlagComponent;
  /** Widened from the library's `Country` union: callers hold plain ISO codes. */
  defaultCountry?: string;
  /** E.164 once the number is complete, `undefined` while it isn't. */
  onChange: (value?: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
};

function PhoneInput({
  className,
  flagComponent,
  defaultCountry,
  // `value`/`onChange` are passed explicitly: the library types its remaining
  // props with an index signature, which TS won't accept as satisfying a
  // required named prop through a rest spread.
  value,
  onChange,
  ...props
}: PhoneInputProps) {
  return (
    <BasePhoneInput
      data-slot="phone-input"
      international
      value={value}
      onChange={onChange}
      defaultCountry={defaultCountry as Country | undefined}
      flagComponent={flagComponent}
      className={cn(
        // The bordered box.
        'flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-transparent pl-3',
        'text-base shadow-sm transition-[color,box-shadow] md:text-sm',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 focus-within:ring-offset-background',
        'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-50',
        // The number input itself owns no chrome — the box above provides it.
        // Written as arbitrary variants so they outrank page-level `input`
        // rules (e.g. `.dnc-add-row input`) that would otherwise re-add a border.
        '[&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:min-w-0 [&_.PhoneInputInput]:flex-1',
        '[&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:p-0',
        '[&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:placeholder:text-muted-foreground',
        className
      )}
      {...props}
    />
  );
}

export { PhoneInput };
